/* eslint-disable perfectionist/sort-imports */
import 'src/global.css';

// ----------------------------------------------------------------------

import ThemeProvider from 'src/theme';

import ProgressBar from 'src/components/progress-bar';
import { SettingsDrawer, SettingsProvider } from 'src/components/settings';
import SnackbarProvider from 'src/components/snackbar/snackbar-provider';

import {
  AC_AUTHORITY,
  CALLBACK_URL,
  POSTLOGOUT_URL,
  SUPERSET_DOMAIN,
  AC_AUTHORITY_CLIENT_SECRET,
} from 'src/config-global';
import {
  MotionLazy,
  ApiProvider,
  AuthProvider,
  useScrollToTop,
  SupersetProvider,
  QueryClientWrapper,
  CookieConsentWrapper,
  CookieConsentProvider,
} from '@emfood/shared';
import { CookiesProvider } from 'react-cookie';
import { mainRouter } from 'src/routes/sections';
import { Outlet, RouterProvider } from '@tanstack/react-router';
import { enqueueSnackbar } from 'notistack';
import { Stack, Typography } from '@mui/material';
import { LocalizationProvider } from './locales';
import { endpoints } from './api/api-config';
import { paths } from './routes/paths';
import { COOKIE_BANNER } from './layouts/config-layout';

export const RootLayout = () => {
  useScrollToTop();

  return (
    <AuthProvider
      currentUserEndpoint={`${endpoints.getCurrentUser}`}
      userManagerSettings={{
        scope: 'openid profile offline_access',
        authority: AC_AUTHORITY,
        client_id: 'ac-marketplace-ui',
        redirect_uri: CALLBACK_URL,
        // Important: The prompt value needs to be configired to match ac-authority
        prompt: 'login consent',
        post_logout_redirect_uri: POSTLOGOUT_URL,
        client_secret: AC_AUTHORITY_CLIENT_SECRET,
        response_type: 'code',
        stateStore: undefined,
      }}
    >
      <SettingsProvider
        defaultSettings={{
          themeMode: 'light', // 'light' | 'dark'
          themeDirection: 'ltr', //  'rtl' | 'ltr'
          themeContrast: 'default', // 'default' | 'bold'
          themeLayout: 'vertical', // 'vertical' | 'horizontal' | 'mini'
          themeColorPresets: 'default', // 'default' | 'cyan' | 'purple' | 'blue' | 'orange' | 'red'
          themeStretch: true,
        }}
      >
        <ThemeProvider>
          <MotionLazy>
            <SnackbarProvider>
              <LocalizationProvider>
                <QueryClientWrapper
                  onError={(data) => {
                    enqueueSnackbar(
                      <Stack sx={{ maxWidth: 800 }}>
                        <Typography fontWeight={600} variant="inherit">
                          {data.message}
                        </Typography>
                        {data.requestId && (
                          <Typography
                            fontSize={11}
                            color="text.secondary"
                            variant="inherit"
                          >{`Request Id: ${data.requestId}`}</Typography>
                        )}
                      </Stack>,
                      { variant: 'error' }
                    );
                  }}
                >
                  <ApiProvider>
                    <SupersetProvider supersetDomain={SUPERSET_DOMAIN} authority={AC_AUTHORITY}>
                      <CookiesProvider>
                        <CookieConsentProvider
                          disabeledPaths={[paths.main.endUserLicenceAgreement, '404']}
                        >
                          <CookieConsentWrapper bannerHeights={COOKIE_BANNER}>
                            <SettingsDrawer />
                            <ProgressBar />
                            <Outlet />
                          </CookieConsentWrapper>
                        </CookieConsentProvider>
                      </CookiesProvider>
                    </SupersetProvider>
                  </ApiProvider>
                </QueryClientWrapper>
              </LocalizationProvider>
            </SnackbarProvider>
          </MotionLazy>
        </ThemeProvider>
      </SettingsProvider>
    </AuthProvider>
  );
};

export default function App() {
  return <RouterProvider router={mainRouter} />;
}
